import { Event as GlobalEvent } from "../../../Global/Base/Events";
import { GenericControl } from "../../UI.BaseControls/GenericControl";
import { createElement, registerCustomElement } from "../../UI.ElementarControls/Utils/CustomElementUtils";
import { CalendarTooltip } from "../CalendarTooltip";
import { ICalendarTooltip } from "../ICalendarTooltip";
import { CalendarEvent } from "./CalendarEvent";
/**
     * Ukázkový kód pro založení prázdného elementu
     */
export class CalendarEventItem extends HTMLElement {
    public readonly mouseOver: GlobalEvent<CalendarEventItem, boolean> = new GlobalEvent<CalendarEventItem, boolean>();

    private _isAttached: boolean;

    private _parent: CalendarEvent;

    private _currentDate: Date;

    private _textElement: HTMLElement;
    private _timeElement: HTMLElement;

    private _type: CalendarEventItemType;

    private _topOffset: number;
    private _bottomOffset: number;

    private _rowNum: number = 0;
    // @ts-ignore // nečteno, ale ponecháno
    private _dblClickListener: any;
    // @ts-ignore // nečteno, ale ponecháno
    private _clickListener: any;
    private _tooltip: ICalendarTooltip;

    //#region Properties
    public set visible(v: boolean) {
        this.classList.toggle("Hidden", !v);
    }

    public get visible(): boolean {
        return !this.classList.contains("Hidden");
    }

    public set enabled(v: boolean) {
        this.classList.toggle("disabled", !v);
    }

    public get enabled(): boolean {
        return !this.classList.contains("disabled");
    }

    public get isAttached(): boolean {
        return this._isAttached;
    }

    public set type(v: CalendarEventItemType) {
        if (this._type) {
            this.classList.remove(CalendarEventItemType[this._type]);
        }
        this._type = v;
        this.classList.add(CalendarEventItemType[this._type]);
    }

    public get type(): CalendarEventItemType {
        return this._type;
    }

    public set topOffset(val: number) {
        this._topOffset = val;
    }

    public get topOffset(): number {
        return this._topOffset;
    }

    public set bottomOffset(val: number) {
        this._bottomOffset = val;
    }

    public get bottomOffset(): number {
        return this._bottomOffset;
    }

    public set mouseOverStyle(hovered: boolean) {
        this.classList.toggle("hover", hovered);
    }

    public get eventId(): string {
        return `${this._currentDate.getHours()}|${this._currentDate.getDate()}|${this._currentDate.getMonth()}|${this._currentDate.getFullYear()}`; // `
    }

    public set currentDate(v: Date) {
        this._currentDate = v;
    }

    public get currentDate(): Date {
        return this._currentDate;
    }

    public set parentEvent(v: CalendarEvent) {
        this._parent = v;
    }

    public get parentEvent(): CalendarEvent {
        return this._parent;
    }

    public set caption(v: string) {
        this._textElement.textContent = v;
    }

    public get caption(): string {
        return this._textElement.textContent;
    }

    public set timeText(v: string) {
        this._timeElement.textContent = v;
    }

    public get timeText(): string {
        return this._timeElement.textContent;
    }

    public set isEmpty(v: boolean) {
        this.classList.toggle("empty", v);
    }

    public get isEmpty(): boolean {
        return this.classList.contains("empty");
    }

    public get rowNum(): number {
        return this._rowNum;
    }

    public set rowNum(v: number) {
        this._rowNum = v;
        if (this.parentEvent)
            this.parentEvent.rowNum = v;
    }
    //#endregion Properties

    public get realRowNum(): number {
        if (this.parentElement == null)
            return -1;
        return Array.from(this.parentElement.children).indexOf(this);
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        GenericControl._constructor(this as HTMLElement);

        this.classList.add("calendar-event");

        const left = createElement({ classes: "left" });
        const right = createElement({ classes: "right" });

        this._textElement = createElement({ classes: "event-name" });
        this._timeElement = createElement({ classes: "event-time" });

        right.appendChild(this._timeElement);
        left.appendChild(this._textElement);

        this.appendChild(left);
        this.appendChild(right);

        this.addEventListener("dblclick", this._dblClickListener = () => {
            this.callEvent("calendarEventItemDblClick", this._parent, true);
        });

        this.addEventListener("click", this._clickListener = (_e: MouseEvent) => {
            this.callEvent("calendarEventItemClick", this._parent, true);
        });

        this.addEventListener("mouseenter",
            () => {
                this.mouseOver.trigger(this, true);
            });
        this.addEventListener("mouseleave",
            () => {
                this.mouseOver.trigger(this, false);
            });
    }

    public set isSelected(v: boolean) {
        this.classList.toggle("selected-record", v);
    }

    // ReSharper disable once InconsistentNaming
    protected connectedCallback(): void {
        this._isAttached = true;
        GenericControl.callEvent(this, "attached");
        if (this._parent && this._parent.dataGrid && this._parent.dataGrid.tooltipCreator) {
            const tooltip = new CalendarTooltip();
            tooltip.init(this, this._parent, this._parent.dataGrid.tooltipCreator, this._parent.canShowTooltipFunction);
            this._tooltip = tooltip;
        }

        if (this._topOffset != undefined) {
            this.style.height = "auto";
            this.style.marginTop = (this.parentElement.offsetHeight * this._topOffset).toString() + "px";
        }
        if (this._bottomOffset != undefined) {
            this.style.height = "auto";
            this.style.marginBottom = (this.parentElement.offsetHeight * this._bottomOffset).toString() + "px";
        }
    }

    // ReSharper disable once InconsistentNaming
    protected disconnectedCallback(): void {
        this._isAttached = false;
        GenericControl.callEvent(this, "detached");
        this.unregisterEventListener();
        if (this._tooltip)
            this._tooltip.dispose();
    }

    // ReSharper disable once InconsistentNaming
    protected attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        GenericControl.attributeChangedCallback(this as HTMLElement, attrName, oldVal, newVal);
    }

    //#region Zpracování metod z globální třídy GenericControl
    /**
     * Zaregistruje událost
     * @param event Název události
     * @param action Akce při vyvolání události
     */
    public registerEventListener(event: string, action: (e: Event) => void): void {
        GenericControl.registerEventListener(this as HTMLElement, event, action);
    }

    /**
     * Odregistruje udalost. Pokud neni vyplnen zadny parametr, jsou odregistrovany vsechny udalosti. Pokud je vyplnen pouze event, odregistruje vsechny udalosti daneho typu
     * pokud je vyplnen event a action, odregistruje pouze konkretni akci
     * @param event Event, ktery chcete odregistrovat.
     * @param action Akce, ktere se tyka event a chcete ji odregistrovat
     */
    public unregisterEventListener(event?: string, action?: (e: Event) => void): void {
        GenericControl.unregisterEventListener(this as HTMLElement, event, action);
    }

    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @return Vrátí customEvent
     */
    public callEvent(eventName: string, detail?: any, bubbles: boolean = false, cancelable: boolean = false): CustomEvent {
        return GenericControl.callEvent(this as HTMLElement, eventName, detail, bubbles, cancelable);
    }

    /**
     * Vrátí pozici elementu v rámci dokumentu
     */
    public offset(): { top: number; left: number; width: number; height: number } {
        return GenericControl.offset(this as HTMLElement);
    }

    //#enregion Zpracování metod z globální třídy GenericControl
}

export enum CalendarEventItemType {
    standalone,
    continuously,
    start,
    end,
    allday,
}

registerCustomElement("tsc-calendargridCalendarEventItem", CalendarEventItem);
